import { unstable_useEnhancedEffect as useEnhancedEffect } from "@mui/material/utils";
import { useCallback, useRef } from "react";
import { OverlayDeparture, overlayGhostStore } from "./overlay-ghost-store";

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
	? Omit<T, K>
	: never;

type DepartureSnapshot = DistributiveOmit<OverlayDeparture, "size" | "surface">;

/**
 * Tracks an overlay so that, when it unmounts while open, a contentless ghost
 * replays its exit transition at its last rendered paper size.
 * Returns a ref for the overlay root (Modal root element, or the Snackbar root).
 */
export const useOverlayPresence = (
	open: boolean,
	paperSelector: string,
	snapshot: DepartureSnapshot,
) => {
	const paperRef = useRef<HTMLElement | null>(null);
	const latestRef = useRef({ open, snapshot });

	useEnhancedEffect(() => {
		latestRef.current = { open, snapshot };
	});

	useEnhancedEffect(() => {
		if (open) overlayGhostStore.reportOpen(latestRef.current.snapshot.kind);
	}, [open]);

	useEnhancedEffect(
		() => () => {
			const { open: wasOpen, snapshot: lastSnapshot } = latestRef.current;
			const paper = paperRef.current;
			if (!wasOpen || !paper) return;

			const size = { width: paper.offsetWidth, height: paper.offsetHeight };
			overlayGhostStore.reportDeparture(
				lastSnapshot.kind === "snackbar"
					? { ...lastSnapshot, size, surface: { className: paper.className } }
					: { ...lastSnapshot, size },
			);
		},
		[],
	);

	return useCallback(
		(root: HTMLElement | null) => {
			// Keep the last paper on detach: it is still attached while unmount cleanups run.
			if (!root) return;
			paperRef.current = root.querySelector<HTMLElement>(paperSelector);
		},
		[paperSelector],
	);
};
